<script setup lang="ts">
import { type HTMLAttributes, computed, ref } from 'vue'
import {
  ScrollAreaCorner,
  ScrollAreaRoot,
  type ScrollAreaRootProps,
  ScrollAreaViewport,
} from 'radix-vue'
import { useScroll } from '@vueuse/core'
import ScrollBar from './ScrollBar.vue'
import { cn } from '@/lib/utils'

const props = defineProps<ScrollAreaRootProps & { 
  class?: HTMLAttributes['class'], 
  thumbClass?: HTMLAttributes['class'],
  showScrollIndicator?: boolean
}>()

// Create ref for the viewport element
const viewportRef = ref<HTMLElement | null>(null)

// Simple reactive scroll position
const scrollTop = ref(0)

// Handle scroll events directly
const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement
  scrollTop.value = target.scrollTop
}

// Computed to check if we're at the top
const isAtTop = computed(() => scrollTop.value === 0)

// Expose scroll information via defineExpose
defineExpose({
  scrollTop,
  isAtTop
})

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props
  return delegated
})
</script>

<template>
  <ScrollAreaRoot v-bind="delegatedProps" :class="cn('relative overflow-hidden', props.class)">
    <ScrollAreaViewport 
      ref="viewportRef"
      class="h-full w-full rounded-[inherit]"
      @scroll="handleScroll"
    >
      <slot />
    </ScrollAreaViewport>
    
    <!-- Scroll indicator with gradient background -->
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div 
        v-if="props.showScrollIndicator && isAtTop"
        class="absolute inset-0 pointer-events-none flex items-end justify-center bg-gradient-to-b from-transparent via-background/40 to-background"
      >
        <div class="w-6 h-6 text-muted-foreground animate-bounce flex items-center justify-center">
          <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </div>
      </div>
    </Transition>
    
    <ScrollBar :thumbClass="props.thumbClass" />
    <ScrollAreaCorner />
  </ScrollAreaRoot>
</template>
